import {UseMutationResult, useMutation, useQueryClient} from '@tanstack/react-query';
import {useDispatch, useSelector} from 'react-redux';
import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDebounce} from 'use-debounce';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {FONT_SIZE, IPresentationTheme} from '../../../../incase-domain/presentationTheme';
import {updatePresentationThemeService} from '../../../../service/presentationTheme.service';
import {setConnectionListPageAction} from '../../../../store/actions/connection.actions';
import {IUpdateError} from '../../../../store/actions/types/error.types';
import {connectionListPageSelector} from '../../../../store/selectors/connection.selectors';
import Modal from '../../../../incase-domain/components/modals/directory/Modal';
import Pagination from '../../../components/common/Pagination';
import Button from '../../../../incase-domain/components/button/Button';
import {useConnectionsPaginatedQuery, toPaginatedResultWrapper} from '../../../../hooks/connectionQueries';

interface PresentationThemeAllowedUsersModalProps {
    isOpen: boolean;
    theme: IPresentationTheme;
    setTheme: (theme: IPresentationTheme) => void;
    setEditUserAccessModal: (modal: boolean) => void;
    initialAllowedUserIds: string[];
}

function PresentationThemeAllowedUsersModal(props: PresentationThemeAllowedUsersModalProps): JSX.Element {
    const {isOpen, theme, setTheme, setEditUserAccessModal, initialAllowedUserIds} = props;
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const connectionListPage = useSelector(connectionListPageSelector);
    const [connectionListSearchTerm, setConnectionListSearchTerm] = useState<string>('');
    const [debouncedSearch] = useDebounce(connectionListSearchTerm, 500);

    const initialFontSizes = [
        {size: FONT_SIZE.EXTRA_SMALL, value: 0.5},
        {size: FONT_SIZE.SMALL, value: 1},
        {size: FONT_SIZE.MEDIUM, value: 1.5},
        {size: FONT_SIZE.LARGE, value: 2},
        {size: FONT_SIZE.EXTRA_LARGE, value: 2.5},
    ];

    function toggleIsSelectedUserId(userId: string): void {
        if (theme.allowedUserIds?.find((user) => user === userId)) {
            const filteredUserIds = theme.allowedUserIds.filter((user) => user !== userId);
            setTheme({...theme, allowedUserIds: filteredUserIds});
        } else {
            setTheme({...theme, allowedUserIds: [...(theme.allowedUserIds || []), userId]});
        }
    }

    const client = useQueryClient();
    const updatePresentationTheme: UseMutationResult<boolean, IUpdateError, {themeId: string; theme: IPresentationTheme}> = useMutation(
        (variables: {themeId: string; theme: IPresentationTheme}) => updatePresentationThemeService(variables.themeId, variables.theme),
        {
            onSuccess: () =>
                client.invalidateQueries({queryKey: QUERY_KEYS.theme.lists()}).then(() => {
                    setEditUserAccessModal(false);
                    setTheme({
                        _id: '',
                        themeName: '',
                        fontSizes: initialFontSizes,
                        fonts: [],
                        defaultFonts: [],
                        lastModified: 0,
                    });
                }),
        }
    );

    const paginatedSearch = useMemo(
        () => ({
            searchString: debouncedSearch,
            pageNumber: connectionListPage,
            pageSize: 5,
        }),
        [debouncedSearch, connectionListPage]
    );

    const connectionsQuery = useConnectionsPaginatedQuery(paginatedSearch);
    const connections = toPaginatedResultWrapper(paginatedSearch, connectionsQuery.data, connectionsQuery.isFetching);

    const onCancel = (): void => {
        setEditUserAccessModal(false);
        setTheme({
            _id: '',
            themeName: '',
            fontSizes: initialFontSizes,
            fonts: [],
            defaultFonts: [],
            lastModified: 0,
        });
        setConnectionListSearchTerm('');
    };

    const onConfirm = (): void => {
        updatePresentationTheme.mutate({
            themeId: theme._id || '',
            theme: {...theme, allowedUserIds: theme.allowedUserIds},
        });
    };

    const hasAllowedUserIdsChanged = useMemo(() => {
        return JSON.stringify(theme.allowedUserIds?.sort()) !== JSON.stringify(initialAllowedUserIds.sort());
    }, [theme.allowedUserIds, initialAllowedUserIds]);

    return (
        <Modal
            isOpen={isOpen}
            showConfirm
            showCancel
            cancelText={`${t('common.cancel')}`}
            confirmText={`${t('common.save')}`}
            onCancel={onCancel}
            onConfirm={onConfirm}
            size="md"
            icon="solar:pallete-2-bold"
            header={theme.themeName}
            description={t('presentationThemesPage.allowUsers')}
            confirmDisabled={!hasAllowedUserIdsChanged}
        >
            <div className="connections">
                <div className="connections-list">
                    <input
                        type="text"
                        className="e-input"
                        placeholder={t('editorPage.sharePresentations.sharePresentationModal.searchConnections')}
                        onChange={(e) => {
                            setConnectionListSearchTerm(e.target.value);
                            dispatch(setConnectionListPageAction(1));
                        }}
                        value={connectionListSearchTerm}
                    />

                    {connections?.content.length ? (
                        <div className="connections-list">
                            <div>
                                {connections?.content?.map((connection) => (
                                    <div className="connections-list-item" key={connection._id}>
                                        <p>{connection.email}</p>
                                        <div>
                                            <Button
                                                variant="soft"
                                                size="sm"
                                                color="secondary"
                                                leading={
                                                    theme.allowedUserIds?.find((user) => user === connection.userId)
                                                        ? 'ph:plus'
                                                        : 'ph:minus'
                                                }
                                                onClick={() => {
                                                    toggleIsSelectedUserId(connection.userId);
                                                }}
                                                disabled={connections.isFetching}
                                            />
                                        </div>
                                    </div>
                                ))}

                                {connections.numberOfElements > connections.pageSize && (
                                    <div className="pagination-wrapper">
                                        <Pagination
                                            numberOfPages={Math.ceil(connections.numberOfElements / connections.pageSize)}
                                            page={connections.pageNumber}
                                            onPageClick={(pageNumber) => {
                                                dispatch(setConnectionListPageAction(pageNumber));
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        connectionListSearchTerm !== '' && (
                            <div className="connections-list">
                                <div className="connections-list-item">
                                    <p>{t('connections.noUsers')}</p>
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>
        </Modal>
    );
}

export default PresentationThemeAllowedUsersModal;
